import { escapeRegExp } from 'common/utilities/regexp/regexp';
import theme from 'core/utilities/theme';

import { arrayTimes } from './array';

export function stringEndsWidth(str: string, endsWidth: string) {
  if (endsWidth.length === 0) return false;
  return str.match(new RegExp(`${endsWidth}$`, 'gi')) !== null;
}

export function splitStringToChunk(str: string | undefined, size: number) {
  if (size === 0) {
    throw new Error('Size needs to be larger then 0');
  }

  return str ? str.match(new RegExp(`.{1,${size}}`, 'g')) : [];
}

export function isStringEmpty(str?: string) {
  return str === null || str === undefined || str.length === 0;
}

// Use a-z and 0-9 characteres, 19 to 23 length
export function generateRandomString() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function generateRandomAZString(length: number) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

// Return a list of indexes where the query match the given string
export function stringMatchAll(str: string, query: string) {
  const regex = new RegExp(escapeRegExp(query), 'g');

  const matchIndexes: number[] = [];

  let match = null;

  do {
    match = regex.exec(str);
    if (match) matchIndexes.push(match.index);
  } while (match !== null);

  return matchIndexes;
}

export function stringContainsLineBreak(str: string) {
  if (str) {
    return str.match(/\n/g) !== null;
  }
  return false;
}

export function stringAcronym(str: string) {
  return (str.match(/\b(\w)/g) || []).join('');
}

export function stringEllipsis(str: string, maxLength: number) {
  return (str.length > maxLength) ? `${str.slice(0, maxLength)}...` : str;
}

/**
 * Replicate a given string n times
 * @param str string to replicate
 * @param times number of times to replicate the string
 */
export function stringTimes(str: string, times: number) {
  return arrayTimes(times).map(() => str).join('');
}

export function stringIsNumber(str?: string) {
  if (str === undefined || str.length === 0 || str.endsWith('.') || str === '-') {
    return false;
  }

  return str.match(new RegExp(
    '^' // No leading content.
    + '(?![Ee])' // Not starting with exponent.
    + '[-+]?' // Optional sign.
    + '(?:[0-9]*\\.)?' // digits and separator.
    + '(?:[0-9]*)' // decimal digits of integer or fraction.
    + '(?:[Ee][-+]?[0-9]{1,3})?' // Optional exponent.
    + '$', // No trailing content.
  )) !== null;
}

/**
 * @see https://stackoverflow.com/questions/2256607/how-to-get-the-next-letter-of-the-alphabet-in-javascript
 * @example A => B, AA => AB, ABC => ABD
 */
export function getNextLetter(str: string) {
  return str.substring(0, str.length - 1)
    + String.fromCharCode(str.charCodeAt(str.length - 1) + 1);
}

/**
 * Create a temporary span element to calculate the given text width
 * It is important to pass the same font and letter spacing to get an accurate size
 *
 * @param text
 * @param font i.e. '500 0.75rem "Roboto", "Helvetica", "Arial", sans-serif'
 * @param letterSpacing i.e. defaults to '0.01071em' set in the theme for text
 */
export function getTextWidth(text: string, font: string, letterSpacing?: string) {
  const tempTextContainer = document.createElement('span');
  const content = document.createTextNode(text);
  tempTextContainer.appendChild(content);

  tempTextContainer.style.position = 'absolute';
  tempTextContainer.style.visibility = 'hidden';
  tempTextContainer.style.font = font;
  tempTextContainer.style.letterSpacing = letterSpacing || theme.typography.body2.letterSpacing as string;

  document.body.appendChild(tempTextContainer);

  const width = tempTextContainer.clientWidth;

  tempTextContainer.remove();

  return width;
}

export function stringRemoveSpaces(str: string) {
  return str.replace(/\s/g, '');
}
